import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    TextField,
    Button,
    Typography,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    IconButton,
    Collapse,
    Box,
    Divider,
    Autocomplete,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

type Props = {
    open: boolean;
    onClose: () => void;
    price?: number;
    totalCoe?: number;
    coeLeft?: number;
    depreciation?: number;
};

const suggestedLoanTerms = [12, 24, 36, 48, 60, 72];

const CarLoanPopup = ({ open, onClose, price = 112800, totalCoe, coeLeft = 60, depreciation }: Props) => {
    const [carPrice, setCarPrice] = useState<string | number>(price);
    const [downType, setDownType] = useState<'percent' | 'amount'>('percent');
    const [downPaymentPercent, setDownPaymentPercent] = useState<string | number>(30);
    const [downPaymentAmount, setDownPaymentAmount] = useState<string | number>(price * 0.3);
    const [interestRate, setInterestRate] = useState<string | number>(2.75);
    const [loanTerm, setLoanTerm] = useState<number>(coeLeft);
    const [inputLoanTerm, setInputLoanTerm] = useState<string>(coeLeft.toString());
    const [showBreakdown, setShowBreakdown] = useState(false);

    useEffect(() => {
        if (!open) {
            setCarPrice(price);
            setDownType('percent');
            setDownPaymentPercent(30);
            setDownPaymentAmount(Number((price * 0.3).toFixed(3)));
            setInterestRate(2.75);
            setLoanTerm(coeLeft);
            setInputLoanTerm(coeLeft.toString());
            setShowBreakdown(false);
        }
    }, [open, price, coeLeft]);

    const numericCarPrice = Number(carPrice) || 0;
    const numericInterestRate = Number(interestRate) || 0;
    const numericPercent = Number(downPaymentPercent) || 0;
    const numericAmount = Number(downPaymentAmount) || 0;

    const downPayment =
        downType === 'percent'
            ? Number(((numericCarPrice * numericPercent) / 100).toFixed(3))
            : Number(numericAmount.toFixed(2));

    const loanAmount = numericCarPrice - downPayment;
    const flatTotalInterest = loanAmount * (numericInterestRate / 100) * (loanTerm / 12);
    const monthlyInstallment = (loanAmount + flatTotalInterest) / loanTerm;
    const totalInterest = flatTotalInterest;
    const totalPaid = loanAmount + totalInterest;

    const formatCurrency = (value: number) =>
        value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const handleDownPaymentChange = (_: React.MouseEvent<HTMLElement>, newValue: number | null) => {
        if (newValue !== null) setDownPaymentPercent(newValue);
    };

    const handleInterestChange = (_: React.MouseEvent<HTMLElement>, newValue: number | null) => {
        if (newValue !== null) setInterestRate(newValue);
    };

    const generateYearlyBreakdown = () => {
        const yearlyData = [];
        const years = Math.ceil(loanTerm / 12);
        const yearlyPrincipal = loanAmount / years;
        const yearlyInterest = flatTotalInterest / years;
        let remaining = loanAmount;

        for (let year = 1; year <= years; year++) {
            remaining -= yearlyPrincipal;
            yearlyData.push({
                year,
                totalPrincipal: yearlyPrincipal,
                totalInterest: yearlyInterest,
                remaining: remaining > 0 ? remaining : 0,
            });
        }

        return yearlyData;
    };

    const yearlyBreakdown = generateYearlyBreakdown();

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Car Loan Calculator
                <IconButton onClick={onClose}><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent>
                <Box display="flex" flexWrap="wrap" gap={4} mt={1}>
                    {/* Left Inputs */}
                    <Box flex={1} minWidth={300}>
                        <Stack spacing={2}>
                            <TextField
                                label="Car Price"
                                type="number"
                                value={carPrice === 0 ? '' : carPrice}
                                onChange={(e) => setCarPrice(e.target.value === '' ? '' : Number(e.target.value))}
                                InputProps={{ startAdornment: <Typography>$</Typography> }}
                                fullWidth
                            />

                            <ToggleButtonGroup
                                exclusive
                                value={downType}
                                onChange={(_, val) => val && setDownType(val)}
                            >
                                <ToggleButton value="percent">By %</ToggleButton>
                                <ToggleButton value="amount">By Amount</ToggleButton>
                            </ToggleButtonGroup>

                            {downType === 'percent' ? (
                                <>
                                    <TextField
                                        label="Down Payment (%)"
                                        type="number"
                                        value={downPaymentPercent === 0 ? '' : downPaymentPercent}
                                        onChange={(e) => setDownPaymentPercent(e.target.value === '' ? '' : Number(e.target.value))}
                                        InputProps={{ endAdornment: <Typography>%</Typography> }}
                                        fullWidth
                                    />
                                    <ToggleButtonGroup
                                        exclusive
                                        value={Number(downPaymentPercent)}
                                        onChange={handleDownPaymentChange}
                                        sx={{ mt: 1 }}
                                    >
                                        <ToggleButton value={10}>10%</ToggleButton>
                                        <ToggleButton value={20}>20%</ToggleButton>
                                        <ToggleButton value={30}>30%</ToggleButton>
                                    </ToggleButtonGroup>
                                </>
                            ) : (
                                <TextField
                                    label="Down Payment ($)"
                                    type="number"
                                    value={downPaymentAmount === 0 ? '' : downPaymentAmount}
                                    onChange={(e) => setDownPaymentAmount(e.target.value === '' ? '' : Number(e.target.value))}
                                    InputProps={{ startAdornment: <Typography>$</Typography> }}
                                    fullWidth
                                />
                            )}

                            <TextField
                                label="Interest Rate"
                                type="number"
                                value={interestRate === 0 ? '' : interestRate}
                                onChange={(e) => setInterestRate(e.target.value === '' ? '' : Number(e.target.value))}
                                InputProps={{ endAdornment: <Typography>%</Typography> }}
                                fullWidth
                            />
                            <ToggleButtonGroup
                                exclusive
                                value={Number(interestRate)}
                                onChange={handleInterestChange}
                                sx={{ mt: 1 }}
                            >
                                <ToggleButton value={2.48}>2.48%</ToggleButton>
                                <ToggleButton value={2.75}>2.75%</ToggleButton>
                                <ToggleButton value={3.18}>3.18%</ToggleButton>
                            </ToggleButtonGroup>

                            <Autocomplete
                                freeSolo
                                options={suggestedLoanTerms.map(String)}
                                inputValue={inputLoanTerm}
                                onInputChange={(_, newInputValue, reason) => {
                                    setInputLoanTerm(newInputValue);
                                    if (reason === 'clear') setLoanTerm(0);
                                    else {
                                        const numeric = Number(newInputValue);
                                        if (!isNaN(numeric)) setLoanTerm(numeric);
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Loan Term (months)" placeholder="e.g. 60" fullWidth />
                                )}
                            />
                        </Stack>
                    </Box>

                    {/* Right Summary */}
                    <Box flex={1} minWidth={300} bgcolor="#f8f8f8" p={3} borderRadius={2}>
                        <Typography fontWeight={600} mb={2}>Car Loan Payment Summary</Typography>

                        <Box display="flex" justifyContent="space-between" mb={1}>
                            <Typography>Down Payment</Typography>
                            <Box textAlign="right">
                                <Typography color="primary">${formatCurrency(downPayment)}</Typography>
                                <Typography variant="caption">
                                    = {downType === 'percent' ? `${numericPercent}% of $${formatCurrency(numericCarPrice)}` : 'Manual Input'}
                                </Typography>
                            </Box>
                        </Box>

                        <Box display="flex" justifyContent="space-between" mb={1}>
                            <Typography>Loan Amount</Typography>
                            <Box textAlign="right">
                                <Typography color="error">${formatCurrency(loanAmount)}</Typography>
                                <Typography variant="caption">= Car Price - Down Payment</Typography>
                            </Box>
                        </Box>

                        <Box display="flex" justifyContent="space-between" mb={1}>
                            <Typography>Monthly Installment</Typography>
                            <Box textAlign="right">
                                <Typography color="error">${formatCurrency(monthlyInstallment)} /mth</Typography>
                                <Typography variant="caption">Based on flat interest formula</Typography>
                            </Box>
                        </Box>

                        <Box display="flex" justifyContent="space-between" mb={1}>
                            <Typography>Total Interest Amount</Typography>
                            <Box textAlign="right">
                                <Typography color="error">${formatCurrency(totalInterest)}</Typography>
                                <Typography variant="caption">= Flat Rate × Term</Typography>
                            </Box>
                        </Box>

                        <Box display="flex" justifyContent="space-between" mb={2}>
                            <Typography>Total Amount</Typography>
                            <Box textAlign="right">
                                <Typography color="error">${formatCurrency(totalPaid)}</Typography>
                                <Typography variant="caption">= Monthly × Term</Typography>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="body2" color="text.secondary" mb={1}>
                            COE Left: {coeLeft || 'N/A'} months | COE: ${totalCoe?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 'N/A'}
                            <br />
                            Depreciation: ${depreciation?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 'N/A'} / year
                        </Typography>

                        <Button variant="outlined" fullWidth sx={{ mt: 1 }} onClick={() => setShowBreakdown(prev => !prev)}>
                            {showBreakdown ? 'Hide Loan Breakdown' : 'View Loan Breakdown'}
                        </Button>

                        {/* 📱 Responsive Breakdown Table */}
                        <Collapse in={showBreakdown}>
                            <Box mt={2} maxHeight={300} overflow="auto" border="1px solid #ddd" borderRadius={1}>
                                {/* Desktop Header */}
                                <Box
                                    display={{ xs: 'none', sm: 'flex' }}
                                    bgcolor="#f0f0f0"
                                    px={2}
                                    py={1}
                                    fontWeight={600}
                                    borderBottom="1px solid #ddd"
                                >
                                    <Box width="20%">Year</Box>
                                    <Box width="25%" textAlign="right">Principal Amount</Box>
                                    <Box width="25%" textAlign="right">Interest Amount</Box>
                                    <Box width="30%" textAlign="right">Remaining Balance</Box>
                                </Box>

                                {yearlyBreakdown.map((item) => (
                                    <Box
                                        key={item.year}
                                        display="flex"
                                        flexDirection={{ xs: 'column', sm: 'row' }}
                                        px={2}
                                        py={1}
                                        fontSize={14}
                                        borderBottom="1px solid #f0f0f0"
                                        gap={{ xs: 0.5, sm: 0 }}
                                    >
                                        {/* Mobile Layout */}
                                        <Box display={{ xs: 'block', sm: 'none' }}>
                                            <Typography fontWeight={600}>Year {item.year}</Typography>
                                            <Typography><strong>Principal:</strong> ${formatCurrency(item.totalPrincipal)}</Typography>
                                            <Typography><strong>Interest:</strong> ${formatCurrency(item.totalInterest)}</Typography>
                                            <Typography><strong>Remaining:</strong> ${formatCurrency(item.remaining)}</Typography>
                                        </Box>

                                        {/* Desktop Layout */}
                                        <Box width="20%" display={{ xs: 'none', sm: 'block' }}>Year {item.year}</Box>
                                        <Box width="25%" textAlign="right" display={{ xs: 'none', sm: 'block' }}>
                                            ${formatCurrency(item.totalPrincipal)}
                                        </Box>
                                        <Box width="25%" textAlign="right" display={{ xs: 'none', sm: 'block' }}>
                                            ${formatCurrency(item.totalInterest)}
                                        </Box>
                                        <Box width="30%" textAlign="right" display={{ xs: 'none', sm: 'block' }}>
                                            ${formatCurrency(item.remaining)}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Collapse>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default CarLoanPopup;
